import { useEffect, useRef, useState } from "react"
import {
    AlmacenArticulo,
    EstadosAlmacenesArticulos,
    EstadosArticulo,
    Order,
    useGetAlmacenArticulosMosaicLazyQuery,
} from "src/gql/schema"
import { FilterSortState } from "src/pages/inventario"
import { MOSAIC_ESTADO_FILTERS_ITEM_ALL, TODOS_ALMACEN_FILTER } from "src/pages/inventario/constants/mosaic-filters"
import { useProfile } from "src/shared/hooks/useProfile"

interface iArticulo {
    [key: number]: { articulos: AlmacenArticulo[]; totalPages: number; currentPage: number }
}

const articulosToShowInitialState = {
    articulos: [],
    currentPage: 1,
    totalPages: 1,
}

const useArticulosMosaicQuery = ({
    page,
    almacenSelected,
    almacenes,
    filterSortState,
}: {
    page: number
    almacenSelected: string
    almacenes: string[]
    filterSortState?: FilterSortState
}) => {
    const [getArticulos] = useGetAlmacenArticulosMosaicLazyQuery()
    const { hotel_id } = useProfile()
    const petitionLoadingRef = useRef<boolean>()

    const [isLoading, setisLoading] = useState(true)
    const [articulos, setarticulos] = useState<iArticulo>({})

    const articulosRef = useRef<iArticulo>({})

    useEffect(() => {
        setarticulos({})
        articulosRef.current = {}
        setarticulosToShow(articulosToShowInitialState)
    }, [filterSortState, almacenSelected])

    const [articulosToShow, setarticulosToShow] = useState<{
        articulos: AlmacenArticulo[]
        totalPages: number
        currentPage: number
    }>(articulosToShowInitialState)

    useEffect(() => {
        setarticulosToShow(articulos?.[almacenSelected] || articulosToShowInitialState)
    }, [page])

    useEffect(() => {
        if (
            articulosRef.current?.[almacenSelected]?.[page] ||
            !almacenes.length ||
            !almacenSelected ||
            petitionLoadingRef.current
        ) {
            return
        }
        petitionLoadingRef.current = true
        setisLoading(true)
        const { filterEstadoAlmacenArticulo, filterEstadoArticulo } = resolveDisponibilidadFilter(filterSortState)

        getArticulos({
            variables: {
                hotel_id,
                almacen_id: filterSortState?.almacenFilters?.filter((f) => f.filter.length).length
                    ? filterSortState?.almacenFilters.map((f) => f.filter)
                    : almacenSelected === TODOS_ALMACEN_FILTER.label
                    ? almacenes
                    : [almacenSelected],
                pagination_options: {
                    take: 12,
                    page,
                    order: Order.Desc,
                },
                filterEstadoArticulo,
                filterEstadoAlmacenArticulo,
            },
        })
            .then((data) => {
                if (!data.data?.almacenes_articulos) {
                    return
                }
                const updatedArticulos = {
                    ...articulos,
                    [almacenSelected]: {
                        currentPage: data.data.almacenes_articulos.paginacion.pagina_actual,
                        totalPages: data.data.almacenes_articulos.paginacion.total_paginas,
                        articulos:
                            data.data?.almacenes_articulos.paginacion.total_paginas > page
                                ? [
                                    ...(articulos?.[almacenSelected]?.articulos || []),
                                    ...((data.data?.almacenes_articulos.almacenes_articulos as AlmacenArticulo[]) ||
                                        []),
                                ]
                                : page === 1
                                ? data.data?.almacenes_articulos.almacenes_articulos
                                : articulos?.[almacenSelected]?.articulos || [],
                    },
                }
                setarticulos(updatedArticulos)
                articulosRef.current = updatedArticulos
                setarticulosToShow(updatedArticulos?.[almacenSelected])
            })
            .finally(() => {
                setisLoading(false)
                petitionLoadingRef.current = false
            })
    }, [page, almacenes])

    const resolveDisponibilidadFilter = (
        filterSortState_?: FilterSortState
    ): {
        filterEstadoArticulo?: EstadosArticulo
        filterEstadoAlmacenArticulo?: EstadosAlmacenesArticulos
    } => {
        const filters = filterSortState_?.disponibilidadFilters?.filter((f) => f.filter.length) ?? []
        if (!filters.length || filters.map(f => f.filter).some(f => f === MOSAIC_ESTADO_FILTERS_ITEM_ALL.value.value)) {
            return {
                filterEstadoArticulo: undefined,
                filterEstadoAlmacenArticulo: undefined,
            }
        }

        const value = filters[0].filter

        if (value === EstadosArticulo.Activado || value === EstadosArticulo.Desactivado) {
            return {
                filterEstadoArticulo: value as EstadosArticulo,
                filterEstadoAlmacenArticulo: undefined,
            }
        }

        return {
            filterEstadoArticulo: undefined,
            filterEstadoAlmacenArticulo: value as EstadosAlmacenesArticulos,
        }
    }

    const refecth = async ({
        almacenSelected_,
        page,
        filterSortState_,
    }: {
        almacenSelected_: string
        page: number
        filterSortState_?: FilterSortState
    }) => {
        if (petitionLoadingRef.current) {
            return
        }
        setisLoading(true)
        petitionLoadingRef.current = true
        const { filterEstadoAlmacenArticulo, filterEstadoArticulo } = resolveDisponibilidadFilter(filterSortState_)

        return await getArticulos({
            variables: {
                hotel_id,
                almacen_id: almacenSelected_ === TODOS_ALMACEN_FILTER.label ? almacenes : [almacenSelected_],
                pagination_options: {
                    take: 12,
                    page,
                    order: Order.Desc,
                },
                filterEstadoArticulo,
                filterEstadoAlmacenArticulo,
            },
        })
            .then((data) => {
                if (!data.data?.almacenes_articulos) {
                    return
                }
                const articulosUpdated = {
                    [almacenSelected_]: {
                        currentPage: data.data.almacenes_articulos.paginacion.pagina_actual,
                        totalPages: data.data.almacenes_articulos.paginacion.total_paginas,
                        articulos: (data.data?.almacenes_articulos.almacenes_articulos as AlmacenArticulo[]) || [],
                    },
                }
                setarticulos(articulosUpdated)
                articulosRef.current = articulosUpdated
                setarticulosToShow(articulosUpdated?.[almacenSelected_])
            })
            .finally(() => {
                setisLoading(false)
                petitionLoadingRef.current = false
            })
    }

    return {
        articulos: articulosToShow.articulos,
        refecth,
        currentPage: articulosToShow.currentPage,
        totalPages: articulosToShow.totalPages,
        isLoading,
    }
}

export default useArticulosMosaicQuery
